import {
  AlertCircle,
  CheckCircle,
  Eye,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  Upload,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils/utils";
import { formatFileSize } from "../../lib/utils/fileUpload";
import { DeleteConfirmationButton } from "../common/ConfirmationDialog";

const actionClassName =
  "inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";

const FileTypeIcon = ({ mimeType, className }) => {
  if (mimeType?.startsWith("image/")) {
    return <FileImage aria-hidden="true" className={className} />;
  }
  if (mimeType === "application/pdf") {
    return <FileText aria-hidden="true" className={className} />;
  }
  if (/spreadsheet|excel|csv/i.test(mimeType || "")) {
    return <FileSpreadsheet aria-hidden="true" className={className} />;
  }
  if (/word|document|text/i.test(mimeType || "")) {
    return <FileText aria-hidden="true" className={className} />;
  }
  return <File aria-hidden="true" className={className} />;
};

const FileUploadRow = ({
  item,
  disabled,
  labels,
  canReplace,
  canRemove = true,
  replacementInProgress,
  showUploadAction = true,
  onPreview,
  onReplace,
  onRetry,
  onRemove,
  onUpload,
}) => {
  const isUploading = item.status === "uploading";
  const isDeleting = item.status === "deleting";
  const hasPreview = Boolean(item.previewUrl || item.url || item.serverId);
  const hasError = Boolean(item.error);
  const removalNoun = item.mimeType?.startsWith("image/") ? "photo" : "file";

  return (
    <div
      className={cn(
        "w-full rounded-lg border bg-white px-3 py-2.5 transition-colors",
        hasError ? "border-red-300 bg-red-50/40" : "border-gray-200",
        disabled && "cursor-not-allowed bg-gray-100 text-gray-500 opacity-70",
      )}
      aria-disabled={disabled || undefined}
      data-status={item.status}
    >
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <FileTypeIcon
            mimeType={item.mimeType}
            className={cn(
              "h-5 w-5 shrink-0",
              hasError ? "text-red-600" : "text-gray-500",
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span
                className="max-w-full truncate text-sm font-medium text-gray-900"
                title={item.name}
                aria-label={item.name}
              >
                {item.name}
              </span>
              {item.size !== undefined && item.size !== null && (
                <span className="shrink-0 text-xs text-gray-500">
                  {formatFileSize(item.size)}
                </span>
              )}
              {isUploading && (
                <span className="shrink-0 text-xs font-medium text-primary" aria-live="polite">
                  {labels.uploading} {item.progress}%
                </span>
              )}
              {isDeleting && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-600" aria-live="polite">
                  <RefreshCw aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
                  {labels.deleting}
                </span>
              )}
            </div>
          </div>
        </div>

        {!disabled && (
          <div className="flex shrink-0 flex-wrap items-center gap-0.5 sm:justify-end">
            {hasPreview && item.status !== "deleting" && (
              <button
                type="button"
                className={actionClassName}
                onClick={() => onPreview(item.localId)}
                aria-label={`${labels.preview} ${item.name}`}
              >
                <Eye aria-hidden="true" className="h-4 w-4" />
                <span>{labels.preview}</span>
              </button>
            )}
            {item.status === "selected" && showUploadAction && (
              <button
                type="button"
                className={actionClassName}
                onClick={() => onUpload(item.localId)}
                aria-label={`${labels.upload} ${item.name}`}
              >
                <Upload aria-hidden="true" className="h-4 w-4" />
                <span>{labels.upload}</span>
              </button>
            )}
            {item.status === "error" && item.file && !item.validationError && (
              <button
                type="button"
                className={actionClassName}
                onClick={() => onRetry(item.localId)}
                aria-label={`${labels.retry} ${item.name}`}
              >
                <RefreshCw aria-hidden="true" className="h-4 w-4" />
                <span>{labels.retry}</span>
              </button>
            )}
            {(item.status === "success" || (item.status === "selected" && !showUploadAction)) && canReplace && !replacementInProgress && (
              <button
                type="button"
                className={actionClassName}
                onClick={() => onReplace(item.localId)}
                aria-label={`${labels.replace} ${item.name}`}
              >
                <RefreshCw aria-hidden="true" className="h-4 w-4" />
                <span>{labels.replace}</span>
              </button>
            )}
            {canRemove && item.status !== "deleting" && !replacementInProgress && (
              <DeleteConfirmationButton
                title={`Remove ${removalNoun}?`}
                description={`Are you sure you want to remove "${item.name}"?`}
                confirmLabel={labels.remove}
                onConfirm={() => onRemove(item.localId)}
              >
                <button
                  type="button"
                  className={cn(actionClassName, "text-red-600 hover:bg-red-50 hover:text-red-700")}
                  aria-label={`${labels.remove} ${item.name}`}
                >
                  <X aria-hidden="true" className="h-4 w-4" />
                  <span>{labels.remove}</span>
                </button>
              </DeleteConfirmationButton>
            )}
          </div>
        )}
      </div>

      {isUploading && (
        <div className="mt-2" aria-live="polite">
          <progress
            className="h-1.5 w-full overflow-hidden rounded-full accent-primary"
            role="progressbar"
            aria-label={`${labels.uploading} ${item.name}`}
            value={item.progress}
            max="100"
          />
          <p className="mt-1.5 text-xs text-gray-500">{labels.pleaseWait}</p>
        </div>
      )}

      {item.status === "success" && !hasError && !item.warning && (
        <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-green-700" aria-live="polite">
          <CheckCircle aria-hidden="true" className="h-4 w-4" />
          {labels.uploadSuccess}
        </p>
      )}
      {item.status === "selected" && !showUploadAction && !hasError && (
        <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-gray-600" aria-live="polite">
          <Upload aria-hidden="true" className="h-4 w-4" />
          {labels.readyToSave}
        </p>
      )}
      {hasError && (
        <p className="mt-1.5 flex items-start gap-1 text-xs font-medium text-red-700" role="alert">
          <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{item.error}</span>
        </p>
      )}
      {item.warning && (
        <p className="mt-1.5 flex items-start gap-1 text-xs font-medium text-amber-700" role="alert">
          <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{item.warning}</span>
        </p>
      )}
    </div>
  );
};

export { FileTypeIcon, FileUploadRow };
